/**
 * Sidebar - Dashboard3 sidebar komponenta
 * 
 * Reusable sidebar sa Dashboard3 dizajnom.
 * Podržava collapsed state, mobile drawer, i sve navigacione linkove.
 * 
 * @example
 * ```tsx
 * <Sidebar isCollapsed={false} onCollapse={() => {}} />
 * <Sidebar isMobile onClose={() => {}} />
 * ```
 */

"use client";

import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { TouchAwareTooltip } from "@/components/ui";
import { IconButton } from "@/components/ui";
import { ConfirmationModal, LOGOUT_CONFIRMATION_LOADING } from "@/components";
import { LogoutTransitionOverlay } from "@/components/auth/LogoutTransitionOverlay";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { useMemo } from "react";
import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineBars3,
  HiOutlineChevronLeft,
  HiOutlineCreditCard,
  HiOutlineUserCircle,
} from "react-icons/hi2";
import { Link } from "@/components";
import {
  getAdminLinks,
  getPortalLinks,
  getPublicNavbarLinks,
  isPublicMarketingRoute,
} from "@/components/shared/sidebar/linkItems";
import { useAuth } from "@/lib/contexts";
import { PageTypeAdmin, PageTypePortal } from "@/lib/types";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useTenantData, usePublicLocationsNavLink, usePublicTicketsNavLink, useLogout } from "@/hooks";
import { useMediaAssetGet } from "@/features/media/hooks";
import { getTenantDisplayName } from "@/lib/utils/tenant";
import { TenantLogo } from "@/components/shared/TenantLogo";
import { SidebarThemeToggle } from "@/components/shared/sidebar/SidebarThemeToggle";
import { getSidebarLogoutButtonStyles } from "@/components/shared/sidebar/sidebarLogoutButtonStyles";
import {
  SidebarNavIcon,
  SidebarNavItemLabel,
  sidebarNavRowAlign,
} from "@/components/shared/sidebar/SidebarNavItemLabel";
import { getDashboardSidebarWidth } from "@/lib/layout/dashboard-sidebar";
import { hexToRgba } from "@/lib/utils/color";
import { fullViewportHeightProps } from "@/lib/layout/viewport";
import { EntryType, MediaGroup } from "@/openapi/generated/models";

export interface SidebarProps {
  /**
   * Whether sidebar is collapsed (desktop only)
   */
  isCollapsed?: boolean;
  /**
   * Callback when collapse button is clicked (desktop only)
   */
  onCollapse?: () => void;
  /**
   * Callback when sidebar should close (mobile only)
   */
  onClose?: () => void;
  /**
   * Whether this is mobile sidebar (drawer)
   */
  isMobile?: boolean;
  /**
   * Whether to show as drawer (mobile menu)
   */
  asDrawer?: boolean;
  /**
   * Callback when drawer should open
   */
  onOpen?: () => void;
  /**
   * Render only the drawer panel (no hamburger trigger). Use with controlled
   * `drawerIsOpen` / `onDrawerClose` when the trigger lives outside a fixed
   * transformed ancestor (e.g. public navbar).
   */
  drawerOnly?: boolean;
  /**
   * Controlled drawer open state (used with `drawerOnly`).
   */
  drawerIsOpen?: boolean;
  /**
   * Controlled drawer close handler (used with `drawerOnly`).
   */
  onDrawerClose?: () => void;
}

export const Sidebar = ({
  isCollapsed = false,
  onCollapse,
  onClose,
  isMobile = false,
  asDrawer = false,
  onOpen,
  drawerOnly = false,
  drawerIsOpen,
  onDrawerClose: onDrawerCloseProp,
}: SidebarProps) => {
  const authUser = useAuth();
  const { data: session } = useSession();
  const user = authUser ?? session?.user ?? null;
  const pathname = usePathname();
  const theme = useThemeTokens();
  const {
    isOpen: isDrawerOpenInternal,
    onOpen: onDrawerOpenInternal,
    onClose: onDrawerCloseInternal,
  } = useDisclosure();
  const isDrawerControlled = drawerIsOpen !== undefined;
  const isOpen = isDrawerControlled ? drawerIsOpen : isDrawerOpenInternal;
  const isAdminPage = pathname?.startsWith("/admin") ?? false;
  const {
    isLogoutModalOpen,
    onLogoutModalOpen,
    onLogoutModalClose,
    handleLogout,
    isLoggingOut,
    showLogoutOverlay,
    logoUrl: logoutLogoUrl,
    tenantData: logoutTenantData,
  } = useLogout({
    callbackUrl: isAdminPage ? PageTypeAdmin.SignIn : PageTypePortal.Login,
  });
  const { tenantData, tenantId } = useTenantData();
  
  const tenantName = getTenantDisplayName(tenantData);
  
  // Stabilize query parameters to prevent query key changes that could cause infinite loops
  const mediaAssetParams = useMemo(
    () => ({
      tenantId,
      ownerType: EntryType.Tenant,
      ownerId: tenantId,
      mediaGroup: MediaGroup.Logo,
    }),
    [tenantId],
  );
  
  const { url: logoUrl } = useMediaAssetGet(
    mediaAssetParams,
    { enabled: !!tenantId && tenantId > 0 },
  );

  const locationsNav = usePublicLocationsNavLink();
  const ticketsNav = usePublicTicketsNavLink();

  const navItems = useMemo(() => {
    const path = pathname ?? "";
    if (isAdminPage) return getAdminLinks(path);
    if (asDrawer || isPublicMarketingRoute(path)) {
      return getPublicNavbarLinks(!!user?.isMember, path, { locationsNav, ticketsNav });
    }
    return getPortalLinks(!!user?.isMember, path);
  }, [isAdminPage, asDrawer, pathname, user?.isMember, locationsNav, ticketsNav]);

  const isProfileSettingsActive =
    pathname?.startsWith(PageTypePortal.ProfileSettings) ?? false;
  const isBalanceActive = pathname === PageTypePortal.Balance;

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    onDrawerCloseProp?.();
    if (!isDrawerControlled) {
      onDrawerCloseInternal();
    }
  };

  const closeOnNavigate = asDrawer || isMobile;

  const handleOpen = () => {
    onOpen?.();
    if (!isDrawerControlled) {
      onDrawerOpenInternal();
    }
  };

  const sidebarContent = (
    <Flex
      direction="column"
      {...fullViewportHeightProps}
      borderRightWidth="1px"
      borderColor={theme.borderGlass.default}
      bg={theme.surfaceGlass.sidebar}
      backdropFilter="blur(14px)"
      position="relative"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: theme.gradientEnabled
          ? (theme.mode === "dark"
            ? `linear-gradient(to right, ${hexToRgba(theme.accent.primary, 0.25)} 0%, ${hexToRgba(theme.accent.primary, 0.15)} 30%, ${hexToRgba(theme.accent.primary, 0.08)} 60%, transparent 100%)`
            : `linear-gradient(to right, ${hexToRgba(theme.accent.primary, 0.14)} 0%, ${hexToRgba(theme.accent.primary, 0.08)} 30%, ${hexToRgba(theme.accent.primary, 0.04)} 60%, transparent 100%)`)
          : "transparent",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <Box
        py={6}
        gap={6}
        flex="1"
        minH={0}
        display="flex"
        flexDirection="column"
        position="relative"
        zIndex={1}
        overflowY="hidden"
        overflowX="hidden"
      >
        <Flex 
          align="center" 
          justify="flex-start" 
          mb={2} 
          position="relative"
          w="100%"
        >
          {/* Client Logo (GHD) - Always shown */}
          <Flex
            align="center"
            justify="flex-start"
            w="100%"
            pl={4}
            position="relative"
          >
            <Box
              flexShrink={0}
              transition="all 0.2s linear"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <TenantLogo
                width={isCollapsed ? 48 : 48}
                height={isCollapsed ? 43 : 43}
                tenantData={tenantData}
                src={logoUrl}
                alt={tenantName}
                priority
                style={{
                  transition: "all 0.2s linear",
                }}
              />
            </Box>
          </Flex>
        </Flex>

        <VStack
          px={isCollapsed ? 2 : 4}
          align={isCollapsed ? "center" : "stretch"}
          spacing={2}
          flex="1"
          minH={0}
          overflowY="auto"
        >
          {navItems.map((item) => {
            const isActive = item.active;
            const linkContent = (
              <Link
                key={item.href}
                href={item.href}
                onClick={closeOnNavigate ? handleClose : undefined}
              >
                <Flex
                  align={sidebarNavRowAlign(isCollapsed)}
                  justify={isCollapsed ? "center" : "flex-start"}
                  px={isCollapsed ? 2 : 3}
                  py={2.5}
                  w={isCollapsed ? "40px" : "100%"}
                  borderRadius="8px"
                  gap={isCollapsed ? 0 : 3}
                  bg={isActive ? theme.accent.primaryBg : "transparent"}
                  color={isActive ? theme.accent.primary : theme.text.secondary}
                  transition="all 0.18s ease-out"
                  sx={{
                    "@media (hover: hover)": {
                      "&:hover": {
                        bg:
                          theme.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(11,13,16,0.08)",
                        color: isActive ? theme.accent.primary : theme.text.primary,
                        transform: isCollapsed ? "scale(1.05)" : "translateX(2px)",
                      },
                    },
                  }}
                  position="relative"
                  boxShadow={
                    isActive
                      ? theme.mode === "dark"
                        ? "none"
                        : "0 0 0 rgba(255,255,255,0.65), 0 4px 12px rgba(11,13,16,0.15)"
                      : "none"
                  }
                >
                  <SidebarNavIcon as={item.icon} />
                  <SidebarNavItemLabel isCollapsed={isCollapsed}>
                    {item.label}
                  </SidebarNavItemLabel>
                </Flex>
              </Link>
            );

            return isCollapsed ? (
              <TouchAwareTooltip key={item.href} label={item.label} placement="right">
                {linkContent}
              </TouchAwareTooltip>
            ) : (
              linkContent
            );
          })}

          {user?.isMember && !isAdminPage && !asDrawer && (
            <>
              {isCollapsed ? (
                <TouchAwareTooltip label="Profile Settings" placement="right">
                  <Link
                    href={PageTypePortal.ProfileSettings}
                    onClick={closeOnNavigate ? handleClose : undefined}
                  >
                    <Flex
                      align="center"
                      justify="center"
                      px={2}
                      py={2.5}
                      w="40px"
                      borderRadius="8px"
                      gap={0}
                      bg={isProfileSettingsActive ? theme.accent.primaryBg : "transparent"}
                      color={isProfileSettingsActive ? theme.accent.primary : theme.text.secondary}
                      transition="all 0.18s ease-out"
                      sx={{
                        "@media (hover: hover)": {
                          "&:hover": {
                            bg:
                              theme.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(11,13,16,0.08)",
                            color: isProfileSettingsActive ? theme.accent.primary : theme.text.primary,
                            transform: "scale(1.05)",
                          },
                        },
                      }}
                    >
                      <Icon as={HiOutlineUserCircle} boxSize={4} />
                    </Flex>
                  </Link>
                </TouchAwareTooltip>
              ) : (
                <Link
                  href={PageTypePortal.ProfileSettings}
                  onClick={closeOnNavigate ? handleClose : undefined}
                >
                  <Flex
                    align={sidebarNavRowAlign(isCollapsed)}
                    justify="flex-start"
                    px={3}
                    py={2.5}
                    w="100%"
                    borderRadius="8px"
                    gap={3}
                    bg={isProfileSettingsActive ? theme.accent.primaryBg : "transparent"}
                    color={isProfileSettingsActive ? theme.accent.primary : theme.text.secondary}
                    transition="all 0.18s ease-out"
                    position="relative"
                    boxShadow={
                      isProfileSettingsActive
                        ? theme.mode === "dark"
                          ? "none"
                          : "0 0 0 rgba(255,255,255,0.65), 0 4px 12px rgba(11,13,16,0.15)"
                        : "none"
                    }
                    sx={{
                      "@media (hover: hover)": {
                        "&:hover": {
                          bg:
                            theme.mode === "dark"
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(11,13,16,0.08)",
                          color: isProfileSettingsActive ? theme.accent.primary : theme.text.primary,
                          transform: "translateX(2px)",
                        },
                      },
                    }}
                  >
                    <SidebarNavIcon as={HiOutlineUserCircle} />
                    <SidebarNavItemLabel compact>Profile Settings</SidebarNavItemLabel>
                  </Flex>
                </Link>
              )}
              {isCollapsed ? (
                <TouchAwareTooltip label="Balance" placement="right">
                  <Link
                    href={PageTypePortal.Balance}
                    onClick={closeOnNavigate ? handleClose : undefined}
                  >
                    <Flex
                      align="center"
                      justify="center"
                      px={2}
                      py={2.5}
                      w="40px"
                      borderRadius="8px"
                      gap={0}
                      bg={isBalanceActive ? theme.accent.primaryBg : "transparent"}
                      color={isBalanceActive ? theme.accent.primary : theme.text.secondary}
                      transition="all 0.18s ease-out"
                      sx={{
                        "@media (hover: hover)": {
                          "&:hover": {
                            bg:
                              theme.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(11,13,16,0.08)",
                            color: isBalanceActive ? theme.accent.primary : theme.text.primary,
                            transform: "scale(1.05)",
                          },
                        },
                      }}
                    >
                      <Icon as={HiOutlineCreditCard} boxSize={4} />
                    </Flex>
                  </Link>
                </TouchAwareTooltip>
              ) : (
                <Link
                  href={PageTypePortal.Balance}
                  onClick={closeOnNavigate ? handleClose : undefined}
                >
                  <Flex
                    align={sidebarNavRowAlign(isCollapsed)}
                    justify="flex-start"
                    px={3}
                    py={2.5}
                    w="100%"
                    borderRadius="8px"
                    gap={3}
                    bg={isBalanceActive ? theme.accent.primaryBg : "transparent"}
                    color={isBalanceActive ? theme.accent.primary : theme.text.secondary}
                    transition="all 0.18s ease-out"
                    position="relative"
                    boxShadow={
                      isBalanceActive
                        ? theme.mode === "dark"
                          ? "none"
                          : "0 0 0 rgba(255,255,255,0.65), 0 4px 12px rgba(11,13,16,0.15)"
                        : "none"
                    }
                    sx={{
                      "@media (hover: hover)": {
                        "&:hover": {
                          bg:
                            theme.mode === "dark"
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(11,13,16,0.08)",
                          color: isBalanceActive ? theme.accent.primary : theme.text.primary,
                          transform: "translateX(2px)",
                        },
                      },
                    }}
                  >
                    <SidebarNavIcon as={HiOutlineCreditCard} />
                    <SidebarNavItemLabel compact>Balance</SidebarNavItemLabel>
                  </Flex>
                </Link>
              )}
            </>
          )}

          {(isMobile || asDrawer) && user ? (
            <Box
              borderTopWidth="1px"
              borderTopColor={theme.borderGlass.default}
              pt={4}
              mt={1}
              w="100%"
            >
              <SidebarThemeToggle isCollapsed={isCollapsed} />
              <Box
                pt={4}
                w="100%"
                display="flex"
                justifyContent={isCollapsed ? "center" : "stretch"}
              >
                {isCollapsed ? (
                  <TouchAwareTooltip label="Log out" placement="right">
                    <Flex
                      as="button"
                      align="center"
                      justify="center"
                      px={2}
                      py={2.5}
                      w="40px"
                      borderRadius="8px"
                      gap={0}
                      {...getSidebarLogoutButtonStyles(theme, true)}
                      onClick={onLogoutModalOpen}
                    >
                      <Icon as={HiOutlineArrowRightOnRectangle} boxSize={4} />
                    </Flex>
                  </TouchAwareTooltip>
                ) : (
                  <Flex
                    as="button"
                    align="center"
                    justify="flex-start"
                    px={3}
                    py={2.5}
                    w="100%"
                    borderRadius="8px"
                    gap={3}
                    {...getSidebarLogoutButtonStyles(theme, false)}
                    onClick={onLogoutModalOpen}
                  >
                    <SidebarNavIcon as={HiOutlineArrowRightOnRectangle} />
                    <SidebarNavItemLabel compact>
                      Log out
                    </SidebarNavItemLabel>
                  </Flex>
                )}
              </Box>
            </Box>
          ) : (isMobile || asDrawer) ? (
            <Box
              borderTopWidth="1px"
              borderTopColor={theme.borderGlass.default}
              pt={4}
              mt={1}
              w="100%"
            >
              <SidebarThemeToggle isCollapsed={isCollapsed} />
            </Box>
          ) : (
            user && (
              <Box
                borderTopWidth="1px"
                borderTopColor={theme.borderGlass.default}
                pt={4}
                mt={3}
                w="100%"
                display="flex"
                justifyContent={isCollapsed ? "center" : "stretch"}
              >
                {isCollapsed ? (
                  <TouchAwareTooltip label="Log out" placement="right">
                    <Flex
                      as="button"
                      align="center"
                      justify="center"
                      px={2}
                      py={2.5}
                      w="40px"
                      borderRadius="8px"
                      gap={0}
                      {...getSidebarLogoutButtonStyles(theme, true)}
                      onClick={onLogoutModalOpen}
                    >
                      <Icon as={HiOutlineArrowRightOnRectangle} boxSize={4} />
                    </Flex>
                  </TouchAwareTooltip>
                ) : (
                  <Flex
                    as="button"
                    align="center"
                    justify="flex-start"
                    px={3}
                    py={2.5}
                    w="100%"
                    borderRadius="8px"
                    gap={3}
                    {...getSidebarLogoutButtonStyles(theme, false)}
                    onClick={onLogoutModalOpen}
                  >
                    <SidebarNavIcon as={HiOutlineArrowRightOnRectangle} />
                    <SidebarNavItemLabel compact>
                      Log out
                    </SidebarNavItemLabel>
                  </Flex>
                )}
              </Box>
            )
          )}
        </VStack>

        <Box
          mt="auto"
          position="relative"
          h="40px"
          display="flex"
          alignItems="center"
          justifyContent={isCollapsed ? "center" : "flex-end"}
          mr={isCollapsed ? 0 : 4}
        >
          {!isMobile && onCollapse && (
            <TouchAwareTooltip
              label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              placement={isCollapsed ? "right" : "top"}
            >
              <IconButton
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                icon={<Icon as={HiOutlineChevronLeft} />}
                size="sm"
                variant="ghost"
                onClick={onCollapse}
                transform={isCollapsed ? "rotate(180deg)" : "none"}
                transition="transform 0.2s"
                color={theme.text.primary}
                bg={
                  theme.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(11,13,16,0.05)"
                }
                sx={{
                  "@media (hover: hover)": {
                    "&:hover": {
                      bg:
                        theme.mode === "dark"
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(11,13,16,0.12)",
                      color: theme.text.primary,
                    },
                  },
                }}
              />
            </TouchAwareTooltip>
          )}
        </Box>
      </Box>
    </Flex>
  );

  const mobileMenuButton = (
    <IconButton
      aria-label="Open menu"
      icon={<Icon as={HiOutlineBars3} />}
      variant="ghost"
      onClick={handleOpen}
      color={theme.text.secondary}
      borderWidth="1px"
      borderColor={theme.borderGlass.default}
      borderRadius="8px"
      sx={{
        WebkitTapHighlightColor: "transparent",
          "@media (hover: hover)": {
          "&:hover": {
            bg: theme.surfaceGlass.cardStrong,
            borderColor: theme.accent.primaryBorder,
            color: theme.accent.primary,
          },
        },
        "@media (hover: none) and (pointer: coarse)": {
          "&:hover, &:active, &:focus, &:focus-visible, &[data-active]": {
            bg: "transparent",
            borderColor: theme.borderGlass.default,
            color: theme.text.secondary,
            boxShadow: "none",
            outline: "none",
          },
        },
      }}
    />
  );

  // If asDrawer, wrap in Drawer component
  if (asDrawer) {
    return (
      <>
        {!drawerOnly && (
          <Box display={{ base: "flex", lg: "none" }}>{mobileMenuButton}</Box>
        )}
        <Drawer
          isOpen={isOpen}
          placement="left"
          onClose={handleClose}
          size="xs"
          portalProps={{ appendToParentPortal: false }}
        >
          <DrawerOverlay
            bg={theme.mode === "dark" ? "rgba(0,0,0,0.8)" : "rgba(11,13,16,0.6)"}
            backdropFilter="blur(8px)"
          />
          <DrawerContent
            maxW={`${getDashboardSidebarWidth(false)}px`}
            {...fullViewportHeightProps}
            overflow="hidden"
            bg={theme.mode === "dark" ? "rgba(11,13,16,0.95)" : "rgba(255,255,255,0.95)"}
            borderRightWidth="1px"
            borderColor={theme.borderGlass.default}
          >
            {sidebarContent}
          </DrawerContent>
        </Drawer>
        <ConfirmationModal
          data-testid="sidebar-drawer-logout-dialog"
          isOpen={isLogoutModalOpen}
          onClose={onLogoutModalClose}
          onConfirm={handleLogout}
          isLoading={isLoggingOut}
          title="Confirm Logout"
          message="Are you sure you want to log out?"
          confirmText="Log out"
          confirmColorScheme="red"
          {...LOGOUT_CONFIRMATION_LOADING}
        />
        <LogoutTransitionOverlay
          isVisible={showLogoutOverlay}
          logoUrl={logoutLogoUrl}
          tenantData={logoutTenantData}
        />
      </>
    );
  }

  return (
    <>
      {sidebarContent}
      <ConfirmationModal
        data-testid="sidebar-logout-dialog"
        isOpen={isLogoutModalOpen}
        onClose={onLogoutModalClose}
        onConfirm={handleLogout}
        isLoading={isLoggingOut}
        title="Potvrdite odjavu"
        message="Da li ste sigurni da želite da se izlogujete?"
        confirmText="Izloguj se"
        confirmColorScheme="red"
        {...LOGOUT_CONFIRMATION_LOADING}
      />
      <LogoutTransitionOverlay
        isVisible={showLogoutOverlay}
        logoUrl={logoutLogoUrl}
        tenantData={logoutTenantData}
      />
    </>
  );
};

